// src/components/Leave/LeaveHistory.jsx - FINAL FIXED VERSION

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch, FiFilter, FiRefreshCw, FiEye, FiEdit2, FiTrash2, FiX,
  FiFileText, FiClock, FiCheckCircle, FiXCircle, FiAlertCircle,
  FiChevronLeft, FiChevronRight
} from 'react-icons/fi';
import { useAppContext } from '../../context/AppContext';
import StatusBadge from '../Common/StatusBadge';

const LeaveHistory = () => {
  const navigate = useNavigate();
  const { leave } = useAppContext();
  const { getMyRequests, loading, deleteLeave } = leave;

  const [leaves, setLeaves] = useState([]);
  const [filteredLeaves, setFilteredLeaves] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const [status, setStatus] = useState('');
  const [leaveType, setLeaveType] = useState('');
  const [search, setSearch] = useState('');

  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
  });

  // Category configuration
  const CATEGORIES = [
    { key: 'all', label: 'All', icon: FiFileText, color: 'gray' },
    { key: 'pending', label: 'Pending', icon: FiClock, color: 'yellow' },
    { key: 'approved', label: 'Approved', icon: FiCheckCircle, color: 'green' },
    { key: 'rejected', label: 'Rejected', icon: FiXCircle, color: 'red' },
    { key: 'draft', label: 'Draft', icon: FiFileText, color: 'gray' },
    { key: 'cancelled', label: 'Cancelled', icon: FiXCircle, color: 'gray' },
    { key: 'withdrawn', label: 'Withdrawn', icon: FiAlertCircle, color: 'orange' },
    { key: 'expired', label: 'Expired', icon: FiAlertCircle, color: 'gray' },
    { key: 'closed', label: 'Closed', icon: FiCheckCircle, color: 'blue' },
  ];

  const colorClasses = {
    yellow: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200',
    green: 'bg-green-100 text-green-700 hover:bg-green-200',
    red: 'bg-red-100 text-red-700 hover:bg-red-200',
    gray: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    orange: 'bg-orange-100 text-orange-700 hover:bg-orange-200',
    blue: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
  };

  const activeColorClasses = {
    yellow: 'bg-yellow-500 text-white',
    green: 'bg-green-500 text-white',
    red: 'bg-red-500 text-white',
    gray: 'bg-gray-600 text-white',
    orange: 'bg-orange-500 text-white',
    blue: 'bg-blue-500 text-white',
  };

  const loadCalled = useRef(false);

  const loadLeaves = useCallback(async (page = 1) => {
    if (isLoading) return;

    setError(null);
    setIsLoading(true);

    try {
      // ✅ Ensure page is a valid positive number
      const validPage = Math.max(1, parseInt(page) || 1);

      const params = {};
      if (status) params.status = status;
      if (leaveType) params.leave_type = leaveType;
      if (search) params.search = search;
      if (validPage > 1) params.page = validPage;


      const data = await getMyRequests(params);

      let leavesData = [];
      let count = 0;

      if (data && typeof data === 'object') {
        if (data.results) {
          leavesData = data.results;
          count = data.count || 0;
        } else if (Array.isArray(data)) {
          leavesData = data;
          count = data.length;
        } else {
          leavesData = [data];
          count = 1;
        }
      }

      setLeaves(leavesData);
      setCurrentPage(validPage);
      setPagination({
        count: count,
        next: data?.next || null,
        previous: data?.previous || null,
      });
      setHasLoaded(true);
      setInitialLoad(false);
    } catch (err) {
      setError(err.message || 'Failed to load leave history');
      setHasLoaded(true);
      setInitialLoad(false);
    } finally {
      setIsLoading(false);
    }
  }, [status, leaveType, search, getMyRequests, isLoading]);

  // Filter leaves by category
  useEffect(() => {
    if (activeCategory === 'all') {
      setFilteredLeaves(leaves);
    } else {
      setFilteredLeaves(leaves.filter(leave => leave.status === activeCategory));
    }
  }, [activeCategory, leaves]);

  // Initial load - only once
  useEffect(() => {
    if (!loadCalled.current) {
      loadCalled.current = true;
      loadLeaves(1);
    }
  }, []);

  // Filter changes - with debounce
  useEffect(() => {
    if (initialLoad) return;

    const timer = setTimeout(() => {
      loadLeaves(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [status, leaveType, search]);

  // Get category count
  const getCategoryCount = (categoryKey) => {
    if (categoryKey === 'all') return leaves.length;
    return leaves.filter(l => l.status === categoryKey).length;
  };

  const handleStatusChange = (e) => setStatus(e.target.value);
  const handleLeaveTypeChange = (e) => setLeaveType(e.target.value);
  const handleSearchChange = (e) => setSearch(e.target.value);

  const clearFilters = () => {
    setStatus('');
    setLeaveType('');
    setSearch('');
  };

  // ✅ SIMPLIFIED FIX: Handle page change by extracting page number
  const handlePageChange = (url) => {
    if (!url) {
      return;
    }


    try {
      let pageNum = null;

      // Try to extract page number from URL
      // Look for pattern: page=123
      const pageMatch = url.match(/[?&]page=(\d+)/);
      if (pageMatch) {
        pageNum = parseInt(pageMatch[1]);

      }

      // If not found, try to extract any number from the URL
      if (!pageNum) {
        const numbers = url.match(/\d+/g);
        if (numbers) {
          // Get the last number (most likely the page number)
          const lastNum = parseInt(numbers[numbers.length - 1]);
          if (!isNaN(lastNum) && lastNum > 0) {
            pageNum = lastNum;

          }
        }
      }

      if (pageNum && !isNaN(pageNum) && pageNum > 0) {

        loadLeaves(pageNum);
      } else {
        ('⚠️ Could not extract page number from URL:', url);
      }
    } catch (err) {
    }
  };

  const handleAction = (action, leaveItem) => {
    if (action === 'view') {
      navigate(`/leave/${leaveItem.id}`);
    } else if (action === 'edit') {
      navigate(`/leave/edit/${leaveItem.id}`);
    } else if (action === 'delete') {
      handleDelete(leaveItem);
    }
  };

  const handleDelete = async (leaveItem) => {
    if (!window.confirm(`Are you sure you want to delete this draft?`)) return;

    try {
      await deleteLeave(leaveItem.id);
      await loadLeaves(currentPage);
    } catch (err) {
      setError(err.message || 'Failed to delete leave');
    }
  };

  // Calculate total pages
  const totalPages = Math.ceil(pagination.count / 10) || 1;
  const hasNext = pagination.next !== null;
  const hasPrevious = pagination.previous !== null;

  // Show loading only on initial load
  if (initialLoad && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // If no data and not loading, show empty state
  if (hasLoaded && leaves.length === 0 && !isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">My Leave History</h1>
            <p className="text-gray-500">View and manage all your leave requests</p>
          </div>
          <button
            onClick={() => navigate('/leave/apply')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm sm:text-base"
          >
            Apply for Leave
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-8 sm:p-12 text-center">
          <div className="text-6xl mb-4">📋</div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">No Leave Requests</h3>
          <p className="text-gray-500 mb-4">You haven't applied for any leave yet.</p>
          <button
            onClick={() => navigate('/leave/apply')}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            Apply for Leave
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">My Leave History</h1>
          <p className="text-sm sm:text-base text-gray-500">View and manage all your leave requests</p>
        </div>
        <button
          onClick={() => navigate('/leave/apply')}
          className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm sm:text-base"
        >
          Apply for Leave
        </button>
      </div>

      {/* Category Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 p-1.5 sm:p-2 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {CATEGORIES.map((category) => {
            const isActive = activeCategory === category.key;
            const count = getCategoryCount(category.key);

            return (
              <button
                key={category.key}
                onClick={() => setActiveCategory(category.key)}
                className={`
                  flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap
                  ${isActive
                    ? (activeColorClasses[category.color] || 'bg-indigo-600 text-white')
                    : (colorClasses[category.color] || 'bg-gray-100 text-gray-700 hover:bg-gray-200')
                  }
                `}
              >
                <category.icon className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden xs:inline">{category.label}</span>
                <span className="xs:hidden">{category.label.slice(0, 3)}</span>
                {count > 0 && (
                  <span className={`
                    inline-flex items-center justify-center px-1.5 sm:px-2 py-0.5 text-xs rounded-full
                    ${isActive ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'}
                  `}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={handleSearchChange}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition flex items-center gap-1 text-sm flex-1 sm:flex-none justify-center"
            >
              <FiFilter className="w-4 h-4" />
              <span className="sm:inline">Filter</span>
            </button>
            <button
              onClick={() => loadLeaves(currentPage)}
              disabled={isLoading}
              className="p-2 text-gray-500 hover:text-indigo-600 transition rounded-lg hover:bg-indigo-50"
            >
              <FiRefreshCw className={`w-4 h-4 sm:w-5 sm:h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={status}
                onChange={handleStatusChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
              >
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
                <option value="withdrawn">Withdrawn</option>
                <option value="expired">Expired</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Leave Type</label>
              <select
                value={leaveType}
                onChange={handleLeaveTypeChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
              >
                <option value="">All Types</option>
                <option value="casual">Casual Leave</option>
                <option value="sick">Sick Leave</option>
                <option value="unpaid">Unpaid Leave</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition flex items-center justify-center gap-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <FiX className="w-4 h-4" />
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
          <button onClick={() => loadLeaves(currentPage)} className="ml-3 text-sm font-medium hover:underline">
            Retry
          </button>
        </div>
      )}

      {/* Table Container */}
      <div id="leave-table-container" className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {filteredLeaves.length === 0 ? (
          <div className="text-center py-12 px-4">
            <div className="text-4xl mb-3">📋</div>
            <p className="text-gray-500 font-medium">
              No {activeCategory !== 'all' ? CATEGORIES.find(c => c.key === activeCategory)?.label : ''} leave requests found
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {activeCategory === 'all' ? 'Apply for leave to see it here' : `You don't have any ${activeCategory} leaves`}
            </p>
            {!status && !leaveType && !search && (
              <button
                onClick={() => navigate('/leave/apply')}
                className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm"
              >
                Apply for Leave
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="sm:hidden divide-y divide-gray-100">
              {filteredLeaves.map((leaveItem) => (
                <div key={leaveItem.id} className="p-4 hover:bg-gray-50 transition">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-500">#{leaveItem.id}</span>
                      <StatusBadge status={leaveItem.status} />
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleAction('view', leaveItem)}
                        className="p-1.5 text-gray-500 hover:text-indigo-600 transition"
                        title="View"
                      >
                        <FiEye className="w-4 h-4" />
                      </button>
                      {leaveItem.can_edit && (
                        <button
                          onClick={() => handleAction('edit', leaveItem)}
                          className="p-1.5 text-gray-500 hover:text-blue-600 transition"
                          title="Edit"
                        >
                          <FiEdit2 className="w-4 h-4" />
                        </button>
                      )}
                      {leaveItem.status === 'draft' && (
                        <button
                          onClick={() => handleAction('delete', leaveItem)}
                          className="p-1.5 text-gray-500 hover:text-red-600 transition"
                          title="Delete"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">
                        {leaveItem.leave_type_display || leaveItem.leave_type}
                      </span>
                      <span className="text-sm font-medium text-gray-700">{leaveItem.total_days} days</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {leaveItem.start_date} → {leaveItem.end_date}
                    </div>
                    <div className="text-xs text-gray-500 line-clamp-1">{leaveItem.reason || 'No reason'}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dates</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredLeaves.map((leaveItem) => (
                    <tr key={leaveItem.id} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">#{leaveItem.id}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {leaveItem.leave_type_display || leaveItem.leave_type}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div>{leaveItem.start_date}</div>
                        <div className="text-xs text-gray-400">→ {leaveItem.end_date}</div>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-700">{leaveItem.total_days}</td>
                      <td className="px-4 py-3">
                        <StatusBadge status={leaveItem.status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleAction('view', leaveItem)}
                            className="p-1 text-gray-500 hover:text-indigo-600 transition"
                            title="View"
                          >
                            <FiEye className="w-4 h-4" />
                          </button>
                          {leaveItem.can_edit && (
                            <button
                              onClick={() => handleAction('edit', leaveItem)}
                              className="p-1 text-gray-500 hover:text-blue-600 transition"
                              title="Edit"
                            >
                              <FiEdit2 className="w-4 h-4" />
                            </button>
                          )}
                          {leaveItem.status === 'draft' && (
                            <button
                              onClick={() => handleAction('delete', leaveItem)}
                              className="p-1 text-gray-500 hover:text-red-600 transition"
                              title="Delete"
                            >
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ✅ FIXED: Simple Pagination with Manual Page Control */}
            {pagination.count > 0 && (
              <div className="px-3 sm:px-6 py-3 sm:py-4 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                  <p className="text-xs sm:text-sm text-gray-500 order-2 sm:order-1">
                    Showing {filteredLeaves.length} of {pagination.count} requests
                  </p>

                  <div className="flex items-center gap-1 sm:gap-2 order-1 sm:order-2 w-full sm:w-auto justify-center">
                    {/* Previous Button - MANUAL PAGE DECREMENT */}
                    <button
                      onClick={() => {
                        if (currentPage > 1) {
                          loadLeaves(currentPage - 1);
                        }
                      }}
                      disabled={currentPage <= 1}
                      className={`
                        flex items-center gap-1 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-sm font-medium transition
                        ${currentPage > 1
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                        }
                      `}
                    >
                      <FiChevronLeft className="w-4 h-4" />
                      <span className="hidden xs:inline">Previous</span>
                      <span className="xs:hidden">Prev</span>
                    </button>

                    {/* Page Info */}
                    <span className="px-3 py-1.5 text-sm text-gray-600 bg-gray-50 rounded-lg">
                      {currentPage} / {totalPages || 1}
                    </span>

                    {/* Next Button - MANUAL PAGE INCREMENT */}
                    <button
                      onClick={() => {
                        if (currentPage < totalPages) {
                          loadLeaves(currentPage + 1);
                        }
                      }}
                      disabled={currentPage >= totalPages}
                      className={`
                        flex items-center gap-1 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-sm font-medium transition
                        ${currentPage < totalPages
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                        }
                      `}
                    >
                      <span className="hidden xs:inline">Next</span>
                      <span className="xs:hidden">Next</span>
                      <FiChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LeaveHistory;